import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useApi } from '@/hooks/useApi';
import { useMembersApi } from '@/hooks/useMembersApi';
import { formatDateShort } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Home, Loader2, Medal, ThumbsUp, Trophy } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

export default function MemberLeaderboard() {
  const membersApi = useMembersApi();
  const { api } = useApi();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [selectedWorkoutId, setSelectedWorkoutId] = useState<string>('');
  const [scope, setScope] = useState<'gym' | 'global'>('gym');
  // Optimistic vote state — keyed by score_id
  const [localVotes, setLocalVotes] = useState<Record<string, boolean>>({});

  // FEAT-APR07-013 — score vote mutation
  const voteMutation = useMutation({
    mutationFn: async (scoreId: string) => {
      const res = await api.post<{ voted: boolean }>(`/scores/${scoreId}/vote`);
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Could not vote');
    },
    onSuccess: (_data, scoreId) => {
      // Invalidate to refetch authoritative state (keeps count accurate).
      queryClient.invalidateQueries({ queryKey: ['member-leaderboard'] });
      // Clear optimistic local state for this id so server truth takes over.
      setLocalVotes((v) => {
        const next = { ...v };
        delete next[scoreId];
        return next;
      });
    },
  });

  const handleVote = (scoreId: string) => {
    setLocalVotes((v) => ({ ...v, [scoreId]: !v[scoreId] }));
    voteMutation.mutate(scoreId);
  };

  // Fetch workouts to get options
  const todayStr = new Date().toISOString().split('T')[0];
  const { data: workoutsData } = useQuery({
    queryKey: ['member-workouts-list', todayStr],
    queryFn: async () => {
      const response = await membersApi.getWorkouts({ limit: 50 });
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  // FEAT-APR30-002 — show scheduled date alongside title to disambiguate
  // recurring workout names (e.g. "Daily WOD – AMRAP" appears multiple times).
  const workoutOptions = useMemo(() => {
    const workouts = workoutsData?.workouts || [];
    return workouts
      .filter((w) => w.scheduled_date)
      .map((w) => ({
        value: w.id,
        label: `${w.name} (${formatDateShort(w.scheduled_date)})`,
      }));
  }, [workoutsData?.workouts]);

  const effectiveWorkoutId = selectedWorkoutId || workoutOptions[0]?.value || '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['member-leaderboard', effectiveWorkoutId, scope],
    queryFn: async () => {
      const response = await membersApi.getLeaderboard(effectiveWorkoutId, { scope });
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    enabled: !!effectiveWorkoutId,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'text-yellow-500';
      case 2:
        return 'text-gray-400';
      case 3:
        return 'text-amber-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank <= 3) {
      return <Trophy className={`h-5 w-5 ${getRankColor(rank)}`} />;
    }
    return <span className="w-5 text-center font-medium">{rank}</span>;
  };

  const formatScore = (value: number, scoringType: string | undefined) => {
    // scoringType comes from the workout: 'time', 'reps', 'rounds', 'points',
    // 'rounds_reps', 'distance', 'calories'. Format accordingly.
    if (scoringType === 'time') {
      const mins = Math.floor(value / 60);
      const secs = value % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    if (scoringType === 'rounds_reps') {
      // Encoded as rounds * 1000 + reps (documented in FEAT-APR07-002 fix).
      const rounds = Math.floor(value / 1000);
      const reps = value % 1000;
      return `${rounds} + ${reps}`;
    }
    if (scoringType === 'distance') return `${value}m`;
    if (scoringType === 'calories') return `${value} cal`;
    return `${value}`;
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load leaderboard</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Leaderboard</h1>
        <p className="mt-1 text-muted-foreground">See how you stack up against other athletes.</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label htmlFor="workout-select" className="text-sm font-medium mb-2 block">
                Workout:
              </label>
              <Select
                id="workout-select"
                options={[{ value: '', label: 'Select a track...' }, ...workoutOptions]}
                value={selectedWorkoutId || effectiveWorkoutId}
                onChange={(e) => setSelectedWorkoutId(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={scope === 'gym' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setScope('gym')}
              >
                <Home className="h-4 w-4 mr-2" />
                My Gym
              </Button>
              <Button
                variant={scope === 'global' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setScope('global')}
              >
                <Globe className="h-4 w-4 mr-2" />
                Global
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Medal className="h-5 w-5 text-primary" />
            Rankings
            <Badge variant="outline" className="ml-2">
              {scope === 'gym' ? 'Gym Only' : 'All Affiliates'}
            </Badge>
            <Badge variant="secondary" className="ml-auto">
              Auto-refreshes every 30s
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data?.leaderboard || data.leaderboard.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Medal className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No scores yet</h3>
              <p className="text-muted-foreground max-w-md">
                {effectiveWorkoutId
                  ? 'No one has logged scores for this workout yet. Be the first!'
                  : 'Select a track to view the leaderboard.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>Athlete</TableHead>
                  {scope === 'global' && <TableHead>Gym</TableHead>}
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead className="text-right w-16">
                    <ThumbsUp className="h-4 w-4 inline" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.leaderboard.map((entry, index) => {
                  const isCurrentUser = entry.user_id === user?.id;
                  const scoreId = entry.score.id;
                  const isVoted = localVotes[scoreId] ?? false;
                  return (
                    <TableRow
                      key={scoreId}
                      className={`animate-fade-in ${
                        entry.rank <= 3 ? 'bg-primary/5' : ''
                      } ${isCurrentUser ? 'ring-2 ring-primary ring-inset' : ''}`}
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <TableCell>
                        <div className="flex items-center justify-center">
                          {getRankIcon(entry.rank)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`font-medium ${isCurrentUser ? 'text-primary' : ''}`}>
                          {entry.user_name}
                          {isCurrentUser && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              You
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      {scope === 'global' && (
                        <TableCell className="text-muted-foreground">
                          {entry.affiliate_name || 'Unknown'}
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span
                            className={`font-mono font-semibold ${
                              entry.rank === 1 ? 'text-primary' : ''
                            }`}
                          >
                            {formatScore(entry.score.value, data?.workout?.scoring_type)}
                          </span>
                          {/* FEAT-APR08-002 — surface pending approval so coaches/owners
                              can see what still needs attention from the leaderboard view. */}
                          {entry.score.approval_status === 'pending' && (
                            <Badge
                              variant="outline"
                              className="text-xs border-yellow-500/50 text-yellow-500"
                            >
                              Pending
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {/* FEAT-APR07-013 — score thumbs-up. Users can't vote on their own scores. */}
                        {!isCurrentUser && (
                          <button
                            type="button"
                            onClick={() => handleVote(scoreId)}
                            disabled={voteMutation.isPending}
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors ${
                              isVoted
                                ? 'bg-primary/20 text-primary'
                                : 'text-muted-foreground hover:text-primary hover:bg-primary/10'
                            }`}
                            aria-label={`Thumbs up ${entry.user_name}'s score`}
                          >
                            <ThumbsUp className="h-3 w-3" />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
